import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Button, DesignV2Scope, EmptyState, SegmentedControl } from "../../../design-v2";
import { StackHeader } from "../../../components/product";
import { muscleLabel } from "../domain/insightsPresentation";
import { CURRENT_ANALYSIS } from "../api/insightsApi";
import {
  E1RM_QUALIFIER,
  PERFORMANCE_BUCKETS_NOTE,
  PERFORMANCE_LEGEND,
  PERFORMANCE_LEGEND_MISSING_RIR,
  PERFORMANCE_NO_DATA,
  PERFORMANCE_TITLE,
  formatAxisLabel,
  formatE1rmHeadline,
  formatE1rmStatus,
  formatRepStatus,
  DETAIL_CARDIO_LINE,
  DETAIL_SESSIONS_TITLE,
  DETAIL_STATE_TITLE,
  formatDetailUnavailable,
  formatHistoricalStateNote,
  formatLatestEvent,
  formatOverviewContext,
  formatRowMeta,
} from "../domain/exercisePerformancePresentation";
import {
  InsightsSection,
  SectionError,
  SectionLoading,
  TimeframeSelector,
} from "../components/InsightsChrome";
import { StateBadge } from "../components/ExercisePerformanceParts";
import ExerciseSessionsList from "../components/ExerciseSessionsList";
import RepRoulette from "../components/RepRoulette";
import PerformanceStepChart from "../components/PerformanceStepChart";
import { insightsPath, useCurrentAnalysis, useExerciseDetail, useTimeframeParam } from "./useInsightsData";

/*
 * Exercise Detail (EP blueprint §6.5 items 1 and 4, §6.6): the Current
 * Analysis state hero and the sessions of the selected timeframe. The state is
 * always Current Analysis (OC-1): on a historical timeframe it stays labelled
 * so and says it does not describe that period. The Performance card (§6.5
 * items 2–3, EP-10) plots the API's points, trends and buckets for one rep
 * count at a time; the e1RM tab exists only when the API says it is visible.
 * Everything shown is the API's: nothing is recomputed here.
 */

const GRAPH_CLASSES = new Set(["PHYSICAL", "LOCAL"]);
const TAB = Object.freeze({ PERFORMANCE: "performance", E1RM: "e1rm" });

function PerformancePane({ performance, series, timeframe, selectionKey, choice, onChoice, dividerDate }) {
  if (!performance?.available) {
    return <p className="lz-insights-ep-note" data-testid="ep-performance-empty">{PERFORMANCE_NO_DATA}</p>;
  }
  const repCounts = performance.repCounts || [];
  const isSelectable = (reps) => repCounts.some((entry) => entry.reps === reps && entry.selectable);
  // The athlete's choice holds for this exercise and timeframe only, and only
  // while it is selectable there; otherwise the API's default.
  const selected = choice.key === selectionKey && isSelectable(choice.reps) ? choice.reps : performance.defaultReps;
  const entry = repCounts.find((candidate) => candidate.reps === selected);
  if (!entry) return <p className="lz-insights-ep-note" data-testid="ep-performance-empty">{PERFORMANCE_NO_DATA}</p>;
  // N − 1 and N + 1: separate series, drawn only when the API gave them a trend.
  const neighbours = [selected - 1, selected + 1]
    .map((reps) => repCounts.find((candidate) => candidate.reps === reps))
    .filter((candidate) => candidate && candidate.trend);
  const monthly = Array.isArray(entry.buckets);
  const historical = timeframe !== CURRENT_ANALYSIS;
  const status = formatRepStatus(entry);
  return (
    <div className="lz-insights-ep-pane" data-testid="ep-performance-pane">
      <RepRoulette repCounts={repCounts} value={selected} onChange={(reps) => onChoice({ key: selectionKey, reps })} />
      <p className="lz-insights-ep-axis" data-testid="ep-axis-label">{formatAxisLabel(series)}</p>
      <PerformanceStepChart
        mode="performance"
        points={entry.points}
        trend={entry.trend}
        neighbours={neighbours}
        buckets={entry.buckets}
        dividerDate={dividerDate}
        unit={series.unit}
        reps={selected}
        summary={`Load at ${selected} reps (${formatAxisLabel(series)})`}
      />
      {status ? <p className="lz-insights-ep-note" data-testid="ep-performance-status">{status}</p> : null}
      <p className="lz-insights-ep-legend" data-testid="ep-performance-legend">
        {monthly ? (
          <><span className="lz-insights-ep-swatch lz-insights-ep-swatch--bucket" aria-hidden="true" />{PERFORMANCE_BUCKETS_NOTE}</>
        ) : (
          // Each colour key sits before its words; the words stay the catalogue's.
          PERFORMANCE_LEGEND.split(" · ").map((part, index) => (
            <span key={part} className="lz-insights-ep-legend__item">
              {index ? " · " : ""}
              <span className={`lz-insights-ep-swatch lz-insights-ep-swatch--${["rir_0_1", "rir_2", "rir_3", "hollow"][index] || "hollow"}`} aria-hidden="true" />
              {part}
            </span>
          ))
        )}
        {!monthly && historical ? (
          <span className="lz-insights-ep-legend__item">
            {" · "}
            <span className="lz-insights-ep-swatch lz-insights-ep-swatch--missing" aria-hidden="true" />
            {PERFORMANCE_LEGEND_MISSING_RIR}
          </span>
        ) : null}
      </p>
    </div>
  );
}

function E1rmPane({ e1rm }) {
  const status = formatE1rmStatus(e1rm);
  return (
    <div className="lz-insights-ep-pane" data-testid="ep-e1rm-pane">
      <p className="lz-insights-ep-headline" data-testid="ep-e1rm-headline">{formatE1rmHeadline(e1rm)}</p>
      <p className="lz-insights-ep-note">{E1RM_QUALIFIER}</p>
      {(e1rm.points || []).length ? (
        <PerformanceStepChart mode="e1rm" points={e1rm.points} trend={e1rm.trend} unit={e1rm.unit} summary={`Estimated 1RM per session (${e1rm.unit === "KG" ? "kg" : "lb"})`} testId="ep-e1rm-chart" />
      ) : null}
      {status ? <p className="lz-insights-ep-note" data-testid="ep-e1rm-status">{status}</p> : null}
    </div>
  );
}

// §6.5 items 2–3: PHYSICAL / LOCAL only (canon §13.1); the tab control only
// when the API makes e1RM visible.
function PerformanceCard({ data, timeframe, exerciseId, tab, onTab, dividerDate }) {
  // The rep choice lives here so that a trip to the e1RM tab keeps it.
  const [choice, setChoice] = useState({ key: null, reps: null });
  if (!data.series || !GRAPH_CLASSES.has(data.series.seriesClass)) return null;
  const e1rmVisible = data.e1rm?.tabVisible === true;
  const current = e1rmVisible && tab === TAB.E1RM ? TAB.E1RM : TAB.PERFORMANCE;
  return (
    <InsightsSection title={PERFORMANCE_TITLE} testId="ep-performance-card">
      {e1rmVisible ? (
        <SegmentedControl
          label="Chart"
          className="lz-insights-ep-tabs"
          value={current}
          onChange={onTab}
          options={[{ value: TAB.PERFORMANCE, label: "Performance" }, { value: TAB.E1RM, label: "e1RM" }]}
        />
      ) : null}
      {current === TAB.E1RM ? (
        <E1rmPane e1rm={data.e1rm} />
      ) : (
        <PerformancePane
          performance={data.performance}
          series={data.series}
          timeframe={timeframe}
          selectionKey={`${exerciseId}|${timeframe}`}
          choice={choice}
          onChoice={setChoice}
          dividerDate={dividerDate}
        />
      )}
    </InsightsSection>
  );
}

const LOCKED = "EXERCISE_PERFORMANCE_LOCKED";

function StateHero({ data, timeframe }) {
  const { state, exercise } = data;
  if (!exercise.analyzed) {
    return <p className="lz-insights-ep-note" data-testid="ep-detail-unavailable">{DETAIL_CARDIO_LINE}</p>;
  }
  const historicalNote = formatHistoricalStateNote(timeframe);
  if (!state?.available) {
    return (
      <InsightsSection title={DETAIL_STATE_TITLE} testId="ep-detail-state">
        <p className="lz-insights-ep-note" data-testid="ep-detail-unavailable">{formatDetailUnavailable(state, exercise)}</p>
      </InsightsSection>
    );
  }
  // Same shape as an overview row so the meta helper reads it unchanged.
  const facts = { state: state.state, comparableExposures: state.comparableExposures, notEnoughData: state.notEnoughData };
  return (
    <InsightsSection
      title={DETAIL_STATE_TITLE}
      subtitle={formatOverviewContext(state.context)}
      testId="ep-detail-state"
      aside={<span className="lz-insights-card__badges"><StateBadge state={state.state} /></span>}
    >
      {state.state !== "NOT_ENOUGH_DATA" ? (
        <p className="lz-insights-ep-line lz-insights-ep-hero__sentence" data-testid="ep-detail-event">{formatLatestEvent(state.latestEvent)}</p>
      ) : null}
      <p className="lz-insights-ep-note" data-testid="ep-detail-meta">{formatRowMeta(facts)}</p>
      {historicalNote ? <p className="lz-insights-ep-note" data-testid="ep-detail-historical-note">{historicalNote}</p> : null}
    </InsightsSection>
  );
}

function NotFound({ onBack }) {
  return (
    <EmptyState
      className="lz-insights-empty"
      title="Exercise not found"
      body="This exercise isn't in the exercise library."
      action={<Button variant="secondary" onClick={onBack}>Back to Exercise Performance</Button>}
      data-testid="ep-detail-not-found"
    />
  );
}

export default function ExerciseDetailScreen() {
  const navigate = useNavigate();
  const { exerciseId } = useParams();
  const [timeframe, setTimeframe] = useTimeframeParam();
  const state = useExerciseDetail(exerciseId, timeframe);
  // Active-cycle start, for the Current Analysis chart divider only (the
  // shared Current Analysis read; the detail response does not carry it).
  const analysis = useCurrentAnalysis();
  const dividerDate = timeframe === CURRENT_ANALYSIS && analysis.status === "success" && analysis.data?.previousCycle
    ? analysis.data.activeCycle?.startDate ?? null
    : null;
  const [tab, setTab] = useState({ exerciseId, value: TAB.PERFORMANCE });
  const back = () => navigate(insightsPath("/progress/exercise-performance", timeframe));

  // Locked (canon §8.1): the feature is hidden; the route returns to the Hub.
  if (state.status === "success" && state.data?.unavailableReason === LOCKED) {
    return <Navigate to={insightsPath("/progress", timeframe)} replace />;
  }

  const data = state.status === "success" ? state.data : null;
  let body;
  if (state.status === "loading") body = <SectionLoading label="Loading exercise" />;
  else if (state.status === "error" && state.error?.status === 404) body = <NotFound onBack={back} />;
  else if (state.status === "error") body = <SectionError what="this exercise" onRetry={state.retry} />;
  else {
    const muscle = data.exercise.primaryNode ? muscleLabel(data.exercise.primaryNode) : null;
    body = (
      <>
        <header className="lz-insights-ep-detail__head">
          <h2 className="lz-insights-ep-detail__name" data-testid="ep-detail-name">{data.exercise.name}</h2>
          {muscle ? <p className="lz-insights-ep-note" data-testid="ep-detail-muscle">{muscle}</p> : null}
        </header>
        <StateHero data={data} timeframe={timeframe} />
        <PerformanceCard
          data={data}
          timeframe={timeframe}
          exerciseId={exerciseId}
          // The chosen tab holds across timeframes, not across exercises.
          tab={tab.exerciseId === exerciseId ? tab.value : TAB.PERFORMANCE}
          onTab={(value) => setTab({ exerciseId, value })}
          dividerDate={dividerDate}
        />
        <InsightsSection title={DETAIL_SESSIONS_TITLE} testId="ep-detail-sessions">
          <ExerciseSessionsList exerciseId={exerciseId} timeframe={timeframe} firstPage={data.sessions} />
        </InsightsSection>
      </>
    );
  }

  return (
    <DesignV2Scope className="lz-insights lz-insights--detail" data-testid="insights-exercise-detail">
      <StackHeader title="Exercise Performance" backLabel="Back to Exercise Performance" onBack={back} />
      <main className="lz-insights__content">
        <TimeframeSelector value={timeframe} onChange={setTimeframe} />
        {body}
      </main>
    </DesignV2Scope>
  );
}
